import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import AuthPageShell from './AuthPageShell';
import './Login.css';

/**
 * Redeems a one-time handoff code on the district subdomain (JWT cannot be shared across origins otherwise).
 */
const SessionImport = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setAuth } = useAuth();
  const [error, setError] = useState('');
  const redeemAttemptedRef = useRef(false);
  const code = useMemo(() => searchParams.get('code'), [searchParams]);
  const nextRaw = useMemo(() => searchParams.get('next') || '/district-admin', [searchParams]);

  useEffect(() => {
    if (redeemAttemptedRef.current) {
      return;
    }

    if (!code) {
      navigate('/login', { replace: true });
      return;
    }
    redeemAttemptedRef.current = true;

    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.post('/api/auth/handoff-session/redeem', { code });
        const token = data?.token;
        const user = data?.user;
        if (!token || !user?.role) {
          if (!cancelled) setError('Invalid response from server.');
          return;
        }
        setAuth(token, user);
        const next = nextRaw.startsWith('/') && !nextRaw.startsWith('//') ? nextRaw : '/';
        navigate(next, { replace: true });
      } catch (e) {
        if (!cancelled) {
          const status = e?.response?.status;
          if (status === 400 || status === 404) {
            setError('Session handoff link is invalid or expired. Please sign in again.');
          } else {
            setError(e.response?.data?.error || e.message || 'Could not import session');
          }
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [code, nextRaw, navigate, setAuth]);

  if (error) {
    return (
      <AuthPageShell>
        <div className="login-box">
          <h1>Back to School Forms</h1>
          <div className="error-message">{error}</div>
          <Link to="/login" className="btn-primary" style={{ display: 'inline-block', textAlign: 'center', marginTop: '1rem', textDecoration: 'none' }}>
            Go to login
          </Link>
        </div>
      </AuthPageShell>
    );
  }

  return (
    <AuthPageShell>
      <div className="login-box">
        <p style={{ textAlign: 'center', color: '#666' }}>Signing you in…</p>
      </div>
    </AuthPageShell>
  );
};

export default SessionImport;
